/**
 * Hooks TanStack Query — drafts générés Claude (Sprint 3).
 *
 * Tous les appels passent par TanStack Query. Pas de `useState` +
 * `useEffect` + `axios.get` — convention CLAUDE.md verrouillée
 * (`fetch(` direct interdit en ESLint).
 *
 * Invalidation : à chaque mutation (generate / approve / regenerate /
 * reject), on invalide la liste des drafts de l'œuvre + son détail.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import {
  draftsApi,
  type BatchStatusResponse,
  type DraftApproveResponse,
  type DraftListFilters,
  type EnqueueBatchResponse,
} from '@/utils/api/drafts'
import { worksKeys } from '@/hooks/useWorks'
import type {
  ApproveDraftRequest,
  DraftListResponse,
  DraftPendingCountResponse,
  DraftRephraseRequest,
  DraftRephraseResponse,
  DraftResponse,
  DraftStatsResponse,
  GenerateDraftsRequest,
  RegenerateDraftRequest,
} from '@/types/drafts'

export const draftsKeys = {
  all: ['drafts'] as const,
  forWork: (workId: number) => [...draftsKeys.all, 'work', workId] as const,
  detail: (id: number) => [...draftsKeys.all, 'detail', id] as const,
  list: (filters: DraftListFilters) => [...draftsKeys.all, 'list', filters] as const,
  pendingCount: () => [...draftsKeys.all, 'pending-count'] as const,
  stats: () => [...draftsKeys.all, 'stats'] as const,
  batchStatus: (batchId: string | null | undefined) =>
    [...draftsKeys.all, 'batch-status', batchId ?? null] as const,
}

type ApiError = { detail: string; status?: number }

// ── Lecture ──────────────────────────────────────────────────────────────────

/**
 * Backoff exponentiel pour le polling render vidéo (S8 PR2).
 *
 * Tant qu'au moins un draft vidéo a `video_id IS NULL` (render en cours),
 * on re-fetch la liste avec un délai croissant 5s → 10s → 20s → 30s plafond.
 * Cohérent avec le plan PR2 §"Frontend PR2 — minimum viable" : pas de
 * websocket nécessaire pour un volume de 1-2 renders/œuvre.
 */
const VIDEO_DRAFT_KINDS = new Set(['instagram_reel', 'tiktok_post'])
const VIDEO_POLL_INTERVALS_MS = [5_000, 10_000, 20_000, 30_000]

function videoRenderInProgress(data: DraftResponse[] | undefined): boolean {
  if (!data) return false
  return data.some(
    (d) =>
      VIDEO_DRAFT_KINDS.has(d.kind) &&
      d.current_version != null &&
      d.current_version.video_id == null,
  )
}

export function useDrafts(workId: number | null | undefined) {
  return useQuery<DraftResponse[]>({
    queryKey: draftsKeys.forWork(workId ?? -1),
    queryFn: () => draftsApi.listForWork(workId as number),
    enabled: workId !== null && workId !== undefined && workId > 0,
    refetchInterval: (query) => {
      if (!videoRenderInProgress(query.state.data)) return false
      // dataUpdateCount = 1, 2, 3, … → indexe le backoff (cap plafond).
      const idx = Math.min(query.state.dataUpdateCount, VIDEO_POLL_INTERVALS_MS.length - 1)
      return VIDEO_POLL_INTERVALS_MS[idx]
    },
    refetchIntervalInBackground: false,
  })
}

// ── Cross-œuvres — page /drafts (S3 câblage) ─────────────────────────────────

/**
 * Liste cross-œuvres pour la page `/drafts` (triage global).
 *
 * `staleTime: 30s` — le triage rafraîchit naturellement entre filtres ;
 * on évite de refetch à chaque montage de page (TanStack par défaut).
 */
export function useAllDrafts(filters: DraftListFilters) {
  return useQuery<DraftListResponse>({
    queryKey: draftsKeys.list(filters),
    queryFn: () => draftsApi.listAll(filters),
    staleTime: 30_000,
  })
}

/**
 * Compteur global pending pour le badge sidebar.
 *
 * Monté dans `<Sidebar>` donc actif sur toutes les pages. Optimisations :
 * - `refetchInterval: 60_000` — poll toutes les minutes
 * - `refetchIntervalInBackground: false` — pause quand l'onglet est inactif
 *   (économise des appels API sur un onglet abandonné)
 * - `refetchOnWindowFocus: true` — rafraîchit au retour sur l'onglet (UX
 *   immédiate, sans attendre le prochain tick du poll)
 */
export function useDraftsPendingCount() {
  return useQuery<DraftPendingCountResponse>({
    queryKey: draftsKeys.pendingCount(),
    queryFn: () => draftsApi.pendingCount(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}

/**
 * Stats Tier 4 par plateforme pour le bandeau KPI de `/drafts`.
 *
 * `staleTime: 5min` — les agrégats bougent lentement, pas de poll.
 */
export function useDraftsStats() {
  return useQuery<DraftStatsResponse>({
    queryKey: draftsKeys.stats(),
    queryFn: () => draftsApi.stats(),
    staleTime: 5 * 60_000,
  })
}

// ── Mutations ────────────────────────────────────────────────────────────────

function invalidateForWork(qc: ReturnType<typeof useQueryClient>, workId: number) {
  void qc.invalidateQueries({ queryKey: draftsKeys.forWork(workId) })
  // Le détail œuvre embarque la liste tags (modifiée par approve avec
  // `accept_suggested_tags`) — on rafraîchit aussi.
  void qc.invalidateQueries({ queryKey: worksKeys.detail(workId) })
  // Cross-keys cross-œuvres : la page /drafts, le badge sidebar et les
  // KPIs Tier 4 doivent refléter le changement immédiatement (sinon le
  // compteur ne décroît qu'au prochain poll 60s = UX décevante "j'ai
  // approuvé, le badge n'a pas bougé"). On invalide le scope `all` —
  // TanStack matche les sous-clés (list/pending-count/stats) automatiquement.
  void qc.invalidateQueries({ queryKey: draftsKeys.all })
}

export function useGenerateDrafts(workId: number) {
  // Refactor 2026-05-15 — la route enqueue maintenant un batch (réponse
  // immédiate). Le caller (WorkDetailPage / WorkCard / DraftsTab) stocke
  // le `batch_id` retourné et passe par `useGenerationBatchPolling` pour
  // suivre l'avancement + déclencher l'invalidation drafts en fin de
  // run.
  return useMutation<EnqueueBatchResponse, ApiError, GenerateDraftsRequest | undefined>({
    mutationFn: (payload) => draftsApi.generate(workId, payload),
  })
}

/**
 * Polling 3s du statut d'un batch de génération.
 *
 * Démarre dès que `batchId` est posé, s'arrête quand `running + scheduled`
 * tombe à 0 (batch terminé : tous les jobs sont `done`/`failed`/`cancelled`).
 *
 * Quand le batch termine, le hook invalide automatiquement
 * `draftsKeys.forWork(workId)` + `draftsKeys.all` + `worksKeys.detail` →
 * la liste drafts se peuple sans intervention du caller. Le caller fournit
 * un `onSettled` optionnel pour afficher un toast bilan
 * (done/failed counts).
 *
 * Limite défensive : auto-stop après 10 min (200 polls × 3s) — protège
 * contre un worker scheduler en panne qui laisserait un batch coincé
 * en `running`. Au-delà, le caller doit refresh la page pour relancer
 * le polling.
 */
const BATCH_POLL_INTERVAL_MS = 3_000
const BATCH_POLL_MAX_DURATION_MS = 10 * 60 * 1000

export interface UseGenerationBatchPollingArgs {
  batchId: string | null | undefined
  workId: number
  onSettled?: (status: BatchStatusResponse) => void
}

export function useGenerationBatchPolling({
  batchId,
  workId,
  onSettled,
}: UseGenerationBatchPollingArgs) {
  const qc = useQueryClient()
  const settledRef = useRef(false)
  const startedAtRef = useRef<number | null>(null)

  // Reset le verrou settled à chaque changement de batchId (nouvelle
  // génération = on doit pouvoir re-déclencher l'invalidation).
  useEffect(() => {
    settledRef.current = false
    startedAtRef.current = batchId ? Date.now() : null
  }, [batchId])

  const query = useQuery<BatchStatusResponse>({
    queryKey: draftsKeys.batchStatus(batchId),
    queryFn: () => draftsApi.getBatchStatus(batchId!),
    enabled: !!batchId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return BATCH_POLL_INTERVAL_MS
      if (data.running + data.scheduled === 0) return false
      if (startedAtRef.current && Date.now() - startedAtRef.current > BATCH_POLL_MAX_DURATION_MS) {
        return false
      }
      return BATCH_POLL_INTERVAL_MS
    },
  })

  // Effet onSettled — une seule fois par batchId quand running+scheduled=0.
  // Pas dans `refetchInterval` (impur). Pas dans `onSuccess` (déprécié v5).
  useEffect(() => {
    const data = query.data
    if (!data || settledRef.current) return
    if (data.running + data.scheduled === 0 && data.total > 0) {
      settledRef.current = true
      invalidateForWork(qc, workId)
      onSettled?.(data)
    }
  }, [query.data, qc, workId, onSettled])

  return query
}

export function useApproveDraft(workId: number) {
  const qc = useQueryClient()
  // PR2 (2026-05-10) — payload désormais REQUIS (mockup_template_ids +
  // work_variant_id) ; retour enrichi `DraftApproveResponse` avec previews.
  return useMutation<
    DraftApproveResponse,
    ApiError,
    { draftId: number; payload: ApproveDraftRequest }
  >({
    mutationFn: ({ draftId, payload }) => draftsApi.approve(draftId, payload),
    onSuccess: () => invalidateForWork(qc, workId),
  })
}

export function useRegenerateDraft(workId: number) {
  const qc = useQueryClient()
  return useMutation<
    DraftResponse,
    ApiError,
    { draftId: number; payload?: RegenerateDraftRequest }
  >({
    mutationFn: ({ draftId, payload }) => draftsApi.regenerate(draftId, payload),
    onSuccess: () => invalidateForWork(qc, workId),
  })
}

export function useRejectDraft(workId: number) {
  const qc = useQueryClient()
  return useMutation<DraftResponse, ApiError, number>({
    mutationFn: (draftId) => draftsApi.reject(draftId),
    onSuccess: () => invalidateForWork(qc, workId),
  })
}

export function useReopenDraft(workId: number) {
  const qc = useQueryClient()
  return useMutation<DraftResponse, ApiError, number>({
    mutationFn: (draftId) => draftsApi.reopen(draftId),
    onSuccess: () => invalidateForWork(qc, workId),
  })
}

// ── Mutations cross-œuvres — appelées depuis /drafts (workId pas connu d'avance) ──

/** Invalide tout le sous-arbre drafts + le detail œuvre de la réponse. */
function invalidateAfterCrossMutation(qc: ReturnType<typeof useQueryClient>, draft: DraftResponse) {
  void qc.invalidateQueries({ queryKey: draftsKeys.all })
  void qc.invalidateQueries({ queryKey: worksKeys.detail(draft.work_id) })
}

export function useApproveDraftCross() {
  const qc = useQueryClient()
  return useMutation<
    DraftApproveResponse,
    ApiError,
    { draftId: number; payload: ApproveDraftRequest }
  >({
    mutationFn: ({ draftId, payload }) => draftsApi.approve(draftId, payload),
    onSuccess: (result) => invalidateAfterCrossMutation(qc, result.draft),
  })
}

export function useRegenerateDraftCross() {
  const qc = useQueryClient()
  return useMutation<
    DraftResponse,
    ApiError,
    { draftId: number; payload?: RegenerateDraftRequest }
  >({
    mutationFn: ({ draftId, payload }) => draftsApi.regenerate(draftId, payload),
    onSuccess: (draft) => invalidateAfterCrossMutation(qc, draft),
  })
}

export function useRejectDraftCross() {
  const qc = useQueryClient()
  return useMutation<DraftResponse, ApiError, number>({
    mutationFn: (draftId) => draftsApi.reject(draftId),
    onSuccess: (draft) => invalidateAfterCrossMutation(qc, draft),
  })
}

export function useReopenDraftCross() {
  const qc = useQueryClient()
  return useMutation<DraftResponse, ApiError, number>({
    mutationFn: (draftId) => draftsApi.reopen(draftId),
    onSuccess: (draft) => invalidateAfterCrossMutation(qc, draft),
  })
}

// ── Rephrase atelier — bouton "Aide-moi à formuler" ──────────────────────────

export function useRephraseField() {
  return useMutation<DraftRephraseResponse, ApiError, DraftRephraseRequest>({
    mutationFn: (payload) => draftsApi.rephrase(payload),
  })
}
